import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/contexts/NotificationContext";
import { User } from "@supabase/supabase-js";
import { Template } from "@/types";
import { EditorMode, TemplateFormData, TemplatePermissions, TemplateNameEditorState } from "../types";
import { generateTemplateFromExample } from "@/integrations/templates/templateService";

export const useTemplateState = (
  mode: EditorMode = "create",
  templateId?: string,
  baseTemplateId?: string
) => {
  const { id: urlId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  // State management
  const [user, setUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [isPromptTouched, setIsPromptTouched] = useState(false);
  const [isAiInstructionDirty, setIsAiInstructionDirty] = useState(false);
  const [nameEditorState, setNameEditorState] = useState<TemplateNameEditorState>({
    isEditingName: false,
    tempName: "Новый шаблон",
    isSavingName: false,
    nameError: null,
  });

  // Состояние отслеживания изменённости шаблона
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [originalData, setOriginalData] = useState<{
    description: string;
    previewTemplateResult: string;
    prompt: string;
  } | null>(null);

  // Ключ для принудительного обновления previewTemplateResult в UnifiedMarkdownRenderer
  const [dataLoadedKey, setDataLoadedKey] = useState(0);

  // Get actual mode and IDs from URL
  const actualMode = urlId
    ? "edit"
    : searchParams.get("baseTemplate")
      ? "create"
      : mode;
  const actualTemplateId = templateId || urlId;
  const actualBaseTemplateId = baseTemplateId || searchParams.get("baseTemplate");

  // Fetch template for edit mode
  const { data: template, isLoading, error } = useQuery({
    queryKey: ["template", actualTemplateId],
    queryFn: async () => {
      if (!actualTemplateId) return null;
      const { data, error } = await supabase.from("templates").select("*").eq("id", actualTemplateId).is("deleted_at", null).single();
      if (error) throw error;

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        prompt: data.prompt,
        preview_template_result: data.preview_template_result,
        previewTemplateResult: data.preview_template_result,
        isSystem: data.is_system,
        is_system: data.is_system,
        createdAt: data.created_at,
        userId: data.user_id,
        deletedAt: data.deleted_at,
      } as Template;
    },
    enabled: !!actualTemplateId && actualMode === "edit",
  });

  // Fetch base template for create mode
  const { data: baseTemplate } = useQuery({
    queryKey: ["baseTemplate", actualBaseTemplateId],
    queryFn: async () => {
      if (!actualBaseTemplateId) return null;
      const { data, error } = await supabase.from("templates").select("*").eq("id", actualBaseTemplateId).is("deleted_at", null).single();
      if (error) throw error;

      return {
        id: data.id,
        name: data.name,
        description: data.description,
        prompt: data.prompt,
        preview_template_result: data.preview_template_result,
        previewTemplateResult: data.preview_template_result,
        isSystem: data.is_system,
        is_system: data.is_system,
        createdAt: data.created_at,
        userId: data.user_id,
        deletedAt: data.deleted_at,
      } as Template;
    },
    enabled: !!actualBaseTemplateId,
  });

  // Form data state
  const [formData, setFormData] = useState<TemplateFormData>({
    templateName: "",
    description: "",
    prompt: "",
    previewTemplateResult: "",
  });

  // Initialize form data based on mode
  useEffect(() => {
    if (actualMode === "edit" && template) {
      const newFormData = {
        templateName: template.name,
        description: template.description,
        prompt: template.prompt,
        previewTemplateResult: template.previewTemplateResult || template.preview_template_result || "",
      };
      setFormData(newFormData);
      setIsPromptTouched(Boolean(template.prompt));
      setIsAiInstructionDirty(false); // Инициализация - при открытии формы считаем что изменений нет
      setNameEditorState(prev => ({
        ...prev,
        tempName: template.name,
      }));

      // Принудительно обновляем ключ для рендеринга previewTemplateResult
      setDataLoadedKey(prev => prev + 1);

      // Устанавливаем исходные данные для отслеживания изменений (только для пользовательских шаблонов)
      if (!template.isSystem) {
        const original = {
          description: template.description,
          previewTemplateResult: template.previewTemplateResult || template.preview_template_result || "",
          prompt: template.prompt,
        };
        setOriginalData(original);
        setIsUnsaved(false);
      }
    } else if (actualMode === "create" && baseTemplate) {
      setFormData({
        templateName: `Копия: ${baseTemplate.name}`,
        description: baseTemplate.description,
        prompt: baseTemplate.prompt,
        previewTemplateResult: baseTemplate.previewTemplateResult || baseTemplate.preview_template_result || "",
      });
      setIsPromptTouched(Boolean(baseTemplate.prompt));
      setIsAiInstructionDirty(false); // Инициализация - при открытии формы считаем что изменений нет

      // Принудительно обновляем ключ для рендеринга previewTemplateResult
      setDataLoadedKey(prev => prev + 1);

      // Для нового шаблона на основе базового - считаем изменённым, т.к. это новая сущность
      setIsUnsaved(true);
    } else if (actualMode === "create") {
      setFormData({
        templateName: "Новый шаблон",
        description: "",
        prompt: "",
        previewTemplateResult: "",
      });
      setIsPromptTouched(false);
      setIsAiInstructionDirty(false); // Инициализация - при открытии формы считаем что изменений нет

      // Для нового шаблона - считаем изменённым, т.к. это новая сущность
      setIsUnsaved(true);
    }
  }, [actualMode, template, baseTemplate]);

  // Get authenticated user
  useEffect(() => {
    const getUserSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session?.user) {
        setUser(data.session.user);
      }
    };
    getUserSession();
  }, []);

  // Calculate permissions based on template and mode
  const permissions: TemplatePermissions = {
    canView: true,
    canEdit: actualMode !== "view" && (!template?.isSystem && (!template?.userId || template.userId === user?.id)),
    canDelete: (actualMode === "edit" || actualMode === "create") && (!template?.isSystem && (!template?.userId || template.userId === user?.id)),
    canCopy: true, // Кнопка копирования доступна всегда, но неактивна для несохраненных шаблонов
    canTest: actualMode !== "view", // Тестировать можно в любом режиме, кроме view
  };

  // Функция для проверки изменений относительно исходных данных
  const checkIfUnsaved = useCallback((
    currentData: TemplateFormData,
    original: typeof originalData
  ): boolean => {
    if (!original || actualMode === "create") return true;

    return (
      currentData.description !== original.description ||
      currentData.previewTemplateResult !== original.previewTemplateResult ||
      currentData.prompt !== original.prompt
    );
  }, [actualMode]);

  // Apply auto-prompt functionality - теперь генерация происходит на сервере
  const applyAutoPrompt = useCallback((data: TemplateFormData) => {
    return {
      ...data,
      prompt: "", // Пустой промпт, сервер сгенерирует его при сохранении
    };
  }, []);

  // Form field update handler
  const updateField = useCallback((
    field: keyof TemplateFormData,
    value: string,
    options?: { markPromptTouched?: boolean }
  ) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value,
      } as TemplateFormData;

      const finalNext = (!isPromptTouched && (field === "description" || field === "previewTemplateResult"))
        ? applyAutoPrompt(next)
        : next;

      // Вычисляем isUnsaved внутри setFormData, где у нас есть доступ к старым и новым данным
      if (actualMode === "edit" && !template?.isSystem && originalData) {
        const hasChanges = checkIfUnsaved(finalNext, originalData);
        setIsUnsaved(hasChanges);
      }

      return finalNext;
    });

    if (field === "prompt") {
      if (options?.markPromptTouched !== false) {
        setIsPromptTouched(true);
        // Отслеживаем изменения в секции "Инструкции для ИИ"
        setIsAiInstructionDirty(true);
      }
    } else if (options?.markPromptTouched) {
      setIsPromptTouched(true);
    }
  }, [isPromptTouched, applyAutoPrompt, actualMode, template?.isSystem, originalData, checkIfUnsaved]);

  // Form validation
  const validateForm = useCallback((): boolean => {
    const { templateName, description } = formData;
    return templateName.trim().length > 0 && description.trim().length > 0;
  }, [formData]);

  // Name editing functions
  const startEditingName = useCallback((currentName?: string) => {
    if (template?.isSystem) return;
    setNameEditorState(prev => ({
      ...prev,
      tempName: currentName || prev.tempName,
      nameError: null,
      isEditingName: true,
    }));
  }, [template?.isSystem]);

  const cancelEditingName = useCallback((currentName?: string) => {
    setNameEditorState(prev => ({
      ...prev,
      isEditingName: false,
      tempName: currentName || formData.templateName,
      nameError: null,
    }));
  }, [formData.templateName]);

  const saveTemplateName = useCallback(async () => {
    if (template?.isSystem) return;

    const { tempName } = nameEditorState;

    if (tempName.length < 3) {
      setNameEditorState(prev => ({
        ...prev,
        nameError: "Название должно содержать минимум 3 символа",
      }));
      return;
    }

    // Если создаем новый шаблон (нет ID), обновляем только локальное состояние
    if (!actualTemplateId) {
      setNameEditorState(prev => ({
        ...prev,
        isEditingName: false,
        nameError: null,
      }));

      setFormData(prev => ({
        ...prev,
        templateName: tempName,
      }));
      return;
    }

    setNameEditorState(prev => ({
      ...prev,
      isSavingName: true,
      nameError: null,
    }));

    try {
      const { error } = await supabase
        .from("templates")
        .update({ name: tempName })
        .eq("id", actualTemplateId);

      if (error) throw error;

      setNameEditorState(prev => ({
        ...prev,
        isEditingName: false,
        isSavingName: false,
      }));

      // Update form data with new name
      setFormData(prev => ({
        ...prev,
        templateName: tempName,
      }));

      queryClient.invalidateQueries({ queryKey: ["template", actualTemplateId] });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ошибка при сохранении";
      setNameEditorState(prev => ({
        ...prev,
        nameError: errorMessage,
        isSavingName: false,
      }));
    }
  }, [template?.isSystem, actualTemplateId, nameEditorState.tempName, queryClient]);

  const updateTempName = useCallback((name: string) => {
    setNameEditorState(prev => ({
      ...prev,
      tempName: name,
    }));
  }, []);

  // Save template mutation
  const saveMutation = useMutation({
    mutationFn: async (templateData: TemplateFormData) => {
      if (!user) throw new Error("Пользователь не авторизован");

      let finalPrompt = templateData.prompt.trim();

      // Если инструкция ИИ не была изменена вручную (isAiInstructionDirty = false),
      // генерируем промпт на основе описания и примера документа
      if (!isAiInstructionDirty) {
        try {
          // Проверяем, что есть и описание, и пример документа
          if (templateData.description.trim() && templateData.previewTemplateResult.trim()) {
            // Проверяем длину примера документа и обрезаем при необходимости
            let documentExample = templateData.previewTemplateResult.trim();
            const maxLength = 10000;

            if (documentExample.length > maxLength) {
              // Обрезаем документ до максимальной длины
              documentExample = documentExample.substring(0, maxLength);

              // Показываем уведомление об обрезке
              addNotification({
                userId: user?.id || "guest",
                type: "warning",
                message: `Пример документа был обрезан до ${maxLength} символов для генерации шаблона`,
              });
            }

            finalPrompt = await generateTemplateFromExample(
              templateData.description.trim(),
              documentExample
            );
          }
          // Если нет данных для генерации, оставляем finalPrompt пустым - сервер сгенерирует
        } catch (error) {
          console.warn("Failed to generate template from example:", error);

          // Проверяем, что это ошибка валидации длины строки
          if (error && typeof error === 'object' && 'detail' in error) {
            const detail = error.detail as Array<{type: string, msg: string}>;
            const lengthError = detail.find(d => d.type === 'string_too_long');

            if (lengthError) {
              addNotification({
                userId: user?.id || "guest",
                type: "error",
                message: "Текст примера документа слишком длинный. Пожалуйста, сократите текст или используйте более короткий пример.",
              });
            } else {
              addNotification({
                userId: user?.id || "guest",
                type: "error",
                message: "Ошибка при генерации шаблона из примера",
              });
            }
          } else {
            addNotification({
              userId: user?.id || "guest",
              type: "error",
              message: "Ошибка при генерации шаблона из примера",
            });
          }

          // В случае ошибки генерации, оставляем finalPrompt пустым
        }
      }
      // Если инструкция была изменена вручную, используем её как есть

      const submitData = {
        description: templateData.description,
        prompt: finalPrompt,
        preview_template_result: templateData.previewTemplateResult,
      };

      const templateName = templateData.templateName.trim() || "Новый шаблон";

      if (actualMode === "edit" && actualTemplateId) {
        const { data, error } = await supabase
          .from("templates")
          .update({
            name: templateName,
            ...submitData,
          })
          .eq("id", actualTemplateId)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from("templates")
          .insert({
            name: templateName,
            ...submitData,
            is_system: false,
            user_id: user.id,
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: (data) => {
      addNotification({
        userId: user?.id || "guest",
        type: "success",
        message: actualMode === "edit" ? "Шаблон успешно обновлен" : "Шаблон успешно создан",
      });

      queryClient.invalidateQueries({ queryKey: ["templates"] });

      // Инвалидируем кеш текущего шаблона для обновления данных
      if (actualMode === "edit" && actualTemplateId) {
        queryClient.invalidateQueries({ queryKey: ["template", actualTemplateId] });
      }

      // Сбрасываем состояние изменённости после успешного сохранения
      if (actualMode === "edit") {
        setIsUnsaved(false);
        setIsAiInstructionDirty(false); // Сбрасываем флаг изменений ИИ-инструкций после сохранения

        // Если промпт был сгенерирован через generateTemplateFromExample,
        // обновляем formData с сохраненными данными для синхронизации
        if (!isAiInstructionDirty && data?.prompt) {
          setFormData(prev => ({
            ...prev,
            prompt: data.prompt,
          }));

          // Обновляем исходные данные с сохраненным промптом
          setOriginalData({
            description: formData.description,
            previewTemplateResult: formData.previewTemplateResult,
            prompt: data.prompt,
          });

          // Устанавливаем флаг, что промпт теперь был изменен (сгенерирован)
          setIsPromptTouched(true);
        } else {
          // Обновляем исходные данные с текущими значениями формы
          setOriginalData({
            description: formData.description,
            previewTemplateResult: formData.previewTemplateResult,
            prompt: formData.prompt,
          });
        }
      }

      if (actualMode !== "edit" && data?.id) {
        window.location.href = `/templates/edit/${data.id}`;
      }
    },
    onError: (error) => {
      console.error("Error saving template:", error);
      addNotification({
        userId: user?.id || "guest",
        type: "error",
        message: "Ошибка при сохранении шаблона",
      });
      // При ошибке сохранения оставляем isUnsaved=true
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from("templates")
        .update({ deleted_at: new Date().toISOString() })
        .eq("id", templateId);
      if (error) throw error;
    },
    onSuccess: () => {
      addNotification({
        userId: user?.id || "guest",
        type: "success",
        message: "Шаблон успешно удален",
      });
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      window.location.href = "/templates";
    },
    onError: (error) => {
      console.error("Error deleting template:", error);
      addNotification({
        userId: user?.id || "guest",
        type: "error",
        message: "Ошибка при удалении шаблона",
      });
    },
  });

  // Duplicate template mutation
  const duplicateTemplateMutation = useMutation({
    mutationFn: async (templateToDuplicate: Template) => {
      const { data, error } = await supabase
        .from("templates")
        .insert({
          name: `${templateToDuplicate.name} (копия)`,
          description: templateToDuplicate.description,
          prompt: templateToDuplicate.prompt,
          preview_template_result: templateToDuplicate.preview_template_result,
          is_system: false,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      addNotification({
        userId: user?.id || "guest",
        type: "success",
        message: "Шаблон успешно скопирован",
      });
      queryClient.invalidateQueries({ queryKey: ["templates"] });

      if (data?.id) {
        window.location.href = `/templates/edit/${data.id}`;
      }
    },
    onError: (error) => {
      console.error("Error duplicating template:", error);
      addNotification({
        userId: user?.id || "guest",
        type: "error",
        message: "Ошибка при копировании шаблона",
      });
    },
  });

  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Предупреждение при закрытии страницы с несохраненными изменениями
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Показываем предупреждение только для пользовательских шаблонов с несохраненными изменениями
      if (actualMode === "edit" && !template?.isSystem && isUnsaved) {
        const message = "У вас есть несохранённые изменения. Вы уверены, что хотите покинуть страницу?";
        e.preventDefault();
        // Для современных браузеров достаточно только preventDefault()
        //returnValue оставлен для совместимости со старыми браузерами
        if ('returnValue' in e) {
          (e as BeforeUnloadEvent & { returnValue?: string }).returnValue = message;
        }
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [actualMode, template?.isSystem, isUnsaved, isAiInstructionDirty]);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      saveMutation.mutate(formData);
    }
  }, [validateForm, saveMutation, formData]);

  // Handle template name change after save
  useEffect(() => {
    if (nameEditorState.isEditingName === false &&
        !nameEditorState.isSavingName &&
        nameEditorState.tempName !== formData.templateName &&
        nameEditorState.tempName !== "Новый шаблон") {
      updateField("templateName", nameEditorState.tempName);
    }
  }, [nameEditorState, formData.templateName, updateField]);

  // Action handlers
  const actions = {
    save: handleSubmit,
    delete: () => setDeleteDialogOpen(true),
    duplicate: () => template && duplicateTemplateMutation.mutate(template),
    test: () => {
      // Template test dialog will handle this
    },
  };

  return {
    // Data
    formData,
    template,
    baseTemplate,
    permissions,
    nameEditorState: {
      ...nameEditorState,
      startEditingName,
      cancelEditingName,
      saveTemplateName,
      updateTempName,
    },

    // State
    isLoading,
    error,
    isMobile,
    showAdvancedSettings,
    deleteDialogOpen,
    isPromptTouched,
    isAiInstructionDirty,
    isUnsaved,
    dataLoadedKey,
    saveMutation,
    deleteTemplateMutation,
    duplicateTemplateMutation,

    // Actions
    actions,
    updateField,
    validateForm,
    setShowAdvancedSettings,
    setDeleteDialogOpen,

    // User
    user,
  };
};
